import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

import {
  Box,
  Grid,
  Typography,
  Avatar,
  Paper,
  useTheme,
  TableRow,
  TableCell,
  Table,
  TableHead,
  TableBody,
  TableContainer
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSackDollar,
  faRuler,
  faPeopleGroup,
  faGlobe,
  faEnvelope,
  faPhone
} from "@fortawesome/pro-solid-svg-icons";

import { DefaultPaper, AnimatedLogo } from "@aclymatepackages/atoms";
import {
  formatDate,
  formatDecimal,
  ucFirstLetters
} from "@aclymatepackages/formatters";
import { sumTonsCo2e, truncateText } from "@aclymatepackages/other-helpers";
import { EmissionsPieChart } from "@aclymatepackages/modules";
import findSubcategory, {
  scopesList,
  buildSubcategoriesArray
} from "@aclymatepackages/subcategories";
import { isColorDarkTheme } from "@aclymatepackages/themes";
import { filterByDateRange } from "@aclymatepackages/date-helpers";
import { subcategories } from "@aclymatepackages/lists";

import ReportLayout from "../../layouts/ReportLayout";

import GrayPaper from "../../atoms/GrayPaper";

import ReportPage from "./ReportPage";
import EmissionsChart from "../charts/EmissionsChart";

import {
  useAccountData,
  useCachedDisplayData
} from "../../../helpers/firebase";
import { scopeThreeSpendBasedCategories } from "../../../helpers/components/scopeThreeCategories";
import {
  useOfficesData,
  useOfficesWithCurrentChanges
} from "../../../helpers/components/offices";
import { REPORT_CONTENT_HEIGHT } from "../../../helpers/components/report";
import { useRevenueYears } from "../../../helpers/hooks/companyData";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import { shippingTransportationMethods } from "../../../helpers/utils/transportationMethods";
import { fetchPeriodTransactions } from "../../../helpers/utils/apiCalls";

dayjs.extend(isBetween);

const filterEmissionsBySubcategory = (emissions, subcategory) =>
  emissions.filter(({ subcategory: subcat }) => subcat === subcategory);

const filterEmissionsByScope = (emissions, scope) =>
  emissions.filter(
    ({ subcategory }) => findSubcategory(subcategory).scope === scope
  );

const splitUtilityEmission = (emission) => {
  const gasTons =
    Number(emission.gasCarbonTons) ||
    Number(emission.monthlyGasCarbonTons) ||
    0;
  const electricTons =
    Number(emission.electricCarbonTons) ||
    Number(emission.monthlyElectricCarbonTons) ||
    0;

  if (!gasTons && !electricTons) {
    return [emission];
  }

  const gasRecord = gasTons
    ? [
        {
          ...emission,
          subcategory: "gas",
          tonsCo2e: gasTons,
          renewableCarbonTonsCo2e: 0
        }
      ]
    : [];
  const electricRecord = electricTons
    ? [
        {
          ...emission,
          subcategory: "electricity",
          tonsCo2e: electricTons
        }
      ]
    : [];

  return [...gasRecord, ...electricRecord];
};

const expandUtilitiesEmissions = (emissions) =>
  emissions.flatMap((emission) =>
    emission.subcategory === "utilities"
      ? splitUtilityEmission(emission)
      : [emission]
  );

const SHIPPING_TRANSPORT_SUBCATEGORIES = [
  "upstream-transport-distribution",
  "downstream-transportation-and-distribution"
];

const findShippingEmissions = (emissions) =>
  Array.isArray(emissions)
    ? emissions.filter(
        ({ subcategory }) =>
          subcategory === "shipping" ||
          SHIPPING_TRANSPORT_SUBCATEGORIES.includes(subcategory)
      )
    : [];

const findShippingDirection = ({
  direction,
  scopeThreeCategory,
  subcategory
}) => {
  if (direction) {
    return direction;
  }
  if (
    subcategory === "upstream-transport-distribution" ||
    scopeThreeCategory === 4
  ) {
    return "upstream";
  }
  return "downstream";
};

const buildShippingMethodRow = (label, methodEmissions) => {
  const upstreamTons = sumTonsCo2e(
    methodEmissions.filter((e) => findShippingDirection(e) === "upstream")
  );
  const downstreamTons = sumTonsCo2e(
    methodEmissions.filter((e) => findShippingDirection(e) === "downstream")
  );
  return {
    name: label,
    upstreamTons,
    downstreamTons,
    totalTons: upstreamTons + downstreamTons
  };
};

const filterOfficesByPeriod = ({ offices, startDate, endDate }) =>
  offices.filter(
    ({ endDate: officeEndDate, startDate: officeStartDate }) =>
      (officeEndDate ? dayjs(officeEndDate).isAfter(startDate) : true) &&
      (officeStartDate ? dayjs(officeStartDate).isBefore(endDate) : true)
  );

const buildReportSubcategoryDescriptions = (companyName, subcategory) => {
  const subcategoryDescriptions = {
    flights: `Flights are trips that any of ${companyName}'s employees take on a commercial airline for business purposes. This includes domestic and international flights.`,
    hotels: `Hotel stays are accommodations booked for employees or guests during business travel. This includes any lodging expenses incurred while traveling for business purposes.`,
    rides:
      "Rides refer to any transportation services used for business purposes, such as ride-sharing or taxi services. This includes trips taken by employees or guests.",
    trains:
      "Train rides refer to any rail transportation used for business purposes. This includes trips taken by employees or guests on passenger trains, such as Amtrak or regional rail",
    mileage:
      "Mileage refers to any drive in a vehicle for business purposes. This includes all miles driven by employees in their personal vehicles when on company business.",
    "company-vehicles": `Company vehicles emissions come from any vehicles owned or leased by ${companyName}'s organization that are used for business purposes. This includes cars, trucks, and vans.`,
    "rental-cars":
      "Leased or personal vehicles emissions come from any automobiles that are rented or personally owned by employees and used for business purposes. This also includes rental cars.",
    "equipment-fuel": `Equipment fuel refers to the fuel consumed by non-road equipment used in ${companyName}'s operations. This includes fuels used in construction, landscaping, or any other machinery.`,
    "employee-commuting":
      "This category includes emissions from employees commuting to and from work using personal vehicles. This includes all miles driven by employees in their personal vehicles.",
    "home-office": `This category includes emissions from employees' home offices during the time that they are working remotely for ${companyName}'s company.`,
    shipping:
      "Shipping emissions refer to the greenhouse gas emissions from the downstream and upstream transportation of goods or products by third-party carriers.",
    waste: `Waste emissions refer to the greenhouse gas emissions from the disposal and treatment of waste generated by ${companyName}'s operations. This includes solid waste, recycling, and composting.`,
    "renewable-energy":
      "Renewable energy usage includes emissions avoided through the procurement of electricity from renewable sources such as solar, wind, or hydroelectric power.",
    "spend-based": `Spend-based emissions refer to the greenhouse gas emissions associated with the purchases made by ${companyName}'s organization. This includes all goods and services purchased.`,
    refrigerants:
      "Refrigerants are chemicals used in cooling systems, such as air conditioners and refrigerators. They can have a high global warming potential (GWP) if released into the atmosphere.",
    utilities:
      "This category includes greenhouse gas emissions from utility providers that supply a combination of gas, electricity, steam, or water.",
    gas: `This category includes the greenhouse gas emissions from natural gas used in ${companyName}'s office buildings. Natural gas is commonly used for heating and cooking.`,
    electricity: `This category includes emissions from the electricity consumed by ${companyName}'s organization's facilities and provided by ${companyName}'s electric utility.`,
    "chill-water":
      "Chill water is a type of cooling fluid used in large commercial and industrial air conditioning systems. It is produced by an electrically-powered chiller and circulated throughout.",
    steam:
      "Steam is a heating medium used in many commercial and industrial applications, including heating buildings and powering equipment.",
    "purchased-water": `Purchased water refers to the water that ${companyName}'s organization buys from a municipal or private water utility.`,
    "purchased-goods-services": `This category of spend-based emissions includes all "consumable" goods and services that are used by ${companyName}'s business, such as office supplies, furniture, and professional services.`,
    "capital-goods": `This category of spend-based emissions includes any capital good that ${companyName} invests in such as production equipment, inventory, and other equipment that's a long-term operations investment.`,
    "upstream-transport-distribution": `This category of spend-based emissions includes emissions from transportation and distribution of goods that are a part of ${companyName}'s business' supply chain.`,
    "downstream-transportation-and-distribution": `This category of spend-based emissions includes emissions from transportation and distribution of ${companyName}'s products to customers.`,
    "business-travel": `This category of spend-based emissions includes emissions from transporting, distributing ${companyName}'s goods downstream from ${companyName}'s facilities to ${companyName}'s customers.`,
    "waste-generated-in-operations": `This category of spend-based emissions includes emissions from waste generated in ${companyName}'s business' operations.`,
    events: `This category includes any emissions related to ${companyName}'s events that aren't otherwise included in activity- or spend-based emissions tagged to ${companyName}'s event, such as facilities rental.`,
    travel:
      "This category includes all emissions related to travel that are not captured in more specific subcategories such as flights, hotel stays, or rides."
  };

  return subcategoryDescriptions[subcategory];
};

const ColoredAvatar = ({ color }) => (
  <div
    style={{
      backgroundColor: color,
      height: "15px",
      width: "15px",
      borderRadius: "50%"
    }}
  />
);

const ColoredAvatarRow = ({ color, text, half }) => (
  <Grid item xs={half ? 6 : 12}>
    <Grid container spacing={1} alignItems="center" wrap="nowrap">
      <Grid item>
        <ColoredAvatar color={color} />
      </Grid>
      <Grid item>
        <Typography variant="caption">{text}</Typography>
      </Grid>
    </Grid>
  </Grid>
);

const ColoredAvatarRows = ({ rows, notHalf }) => (
  <Grid container spacing={0.5}>
    {rows.map((row, key) => (
      <ColoredAvatarRow
        half={!notHalf && rows.length > 2}
        key={`color-avatar-row-${key}`}
        {...row}
      />
    ))}
  </Grid>
);

const PageTitle = ({ children }) => (
  <Typography variant="h3" align="center" gutterBottom color="textPrimary">
    {children}
  </Typography>
);

const PageHeaderText = ({ title, style = {}, children }) => (
  <div style={style}>
    <PageTitle>{title}</PageTitle>
    <Typography variant="body2" align="center" color="textSecondary">
      {children}
    </Typography>
  </div>
);

const TitlePage = ({ startDate, endDate }) => {
  const [{ branding, name }] = useAccountData();
  const { logoUrl } = branding || {};

  return (
    <Box style={{ height: "100%" }} display="flex" alignItems="center">
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <Box display="flex" justifyContent="center">
            <img
              src={logoUrl}
              alt="company logo"
              crossOrigin="anonymous"
              style={{ width: "400px", margin: "auto" }}
            />
          </Box>
        </Grid>
        <Grid item>
          <Typography variant="h3" align="center" color="textPrimary">
            {name} Carbon Emissions Report
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="h5" align="center" color="textSecondary">
            Reporting period: {formatDate(startDate)} -{" "}
            {formatDate(dayjs(endDate).isAfter(dayjs()) ? new Date() : endDate)}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const EmissionsSummaryPage = ({
  emissions,
  startDate,
  endDate,
  totalReportPeriodTons,
  offices,
  graphPeriod
}) => {
  const { palette } = useTheme();
  const { employees } = useEmissionsContext();

  const [revenueYears] = useRevenueYears();

  const [{ branding, employeeCount }] = useAccountData();
  const { companyStatement } = branding || {};

  const coloredAvatarRows = scopesList.map(({ name, color }) => {
    const scopeTonsSum = sumTonsCo2e(filterEmissionsByScope(emissions, name));

    return {
      color: palette[color].main,
      text: `Total Scope ${name} Emissions: ${formatDecimal(
        scopeTonsSum
      )} tons CO2e${!scopeTonsSum ? " *" : ""}`,
      scopeTonsSum
    };
  });

  const findRevenueEmissionIntensity = () => {
    if (!revenueYears) {
      return [];
    }
    const revenueYear = revenueYears.find(
      ({ year }) => year === dayjs(startDate).year()
    );

    if (!revenueYear) {
      return [];
    }

    const { revenue } = revenueYear;
    if (!revenue) {
      return [];
    }

    if (revenue < 1000000) {
      const revenueNearest100K = Math.round(revenue / 100000);
      const emissionsPer100K = totalReportPeriodTons / revenueNearest100K;
      return [
        {
          icon: faSackDollar,
          text: `${formatDecimal(emissionsPer100K)} tCO2e/ $100K revenue`
        }
      ];
    }

    const revenueNearestMillion = Math.round(revenue / 1000000);
    const emissionsPerMillion = totalReportPeriodTons / revenueNearestMillion;

    return [
      {
        icon: faSackDollar,
        text: `${formatDecimal(emissionsPerMillion)} tCO2e/ $1M revenue`
      }
    ];
  };

  const findEmployeeEmissionIntensity = () => {
    const employeesThisPeriod = employees.filter(
      ({ startDate: employeeStartDate, endDate: employeeEndDate }) =>
        (employeeStartDate
          ? dayjs(employeeStartDate).isBefore(endDate)
          : true) &&
        (employeeEndDate ? dayjs(employeeEndDate).isAfter(startDate) : true)
    );

    const numberOfEmployees = employeeCount || employeesThisPeriod.length;

    const emissionsPerEmployee = totalReportPeriodTons / numberOfEmployees;

    return {
      icon: faPeopleGroup,
      text: `${formatDecimal(emissionsPerEmployee)} tCO2e/ Employee`
    };
  };

  const findAreaEmissionIntensity = () => {
    const employeeHomeOffices = employees
      .map(({ commuteSchedules = [] }) => {
        if (!commuteSchedules.length) {
          return null;
        }
        return commuteSchedules.map(({ home }) => {
          if (!home) {
            return null;
          }
          const { homeOfficeArea } = home;
          return homeOfficeArea;
        });
      })
      .flat()
      .filter((area) => !!area);

    const totalHomeOfficeArea = employeeHomeOffices.reduce(
      (sum, area) => sum + Number(area || 0),
      0
    );
    const totalOfficeArea = offices.reduce(
      (sum, { sqFootage }) => sum + Number(sqFootage || 0),
      0
    );

    const totalSqFootage = totalOfficeArea + totalHomeOfficeArea;

    const carbonTonsPerSqFt = totalReportPeriodTons / totalSqFootage;

    return {
      icon: faRuler,
      text: `${formatDecimal(
        carbonTonsPerSqFt,
        4
      )} tCO2e/ sq. ft. of user occupied space`
    };
  };

  const carbonIntensities = [
    ...findRevenueEmissionIntensity(),
    findEmployeeEmissionIntensity(),
    findAreaEmissionIntensity()
  ];

  return (
    <Grid container spacing={2} direction="column">
      <Grid item>
        <PageHeaderText title="Company Statement">
          {companyStatement}
        </PageHeaderText>
      </Grid>
      <Grid item>
        <Typography variant="h5" align="center" gutterBottom>
          Emissions Summary
        </Typography>
        <GrayPaper>
          <Grid container spacing={2} direction="column">
            <Grid item container spacing={2} wrap="nowrap" alignItems="stretch">
              <Grid item xs={2.5}>
                <DefaultPaper>
                  <Grid container direction="column" spacing={1}>
                    {scopesList.map(({ name, color }, idx) => (
                      <ColoredAvatarRow
                        color={palette[color].main}
                        key={`scope-row-${idx}`}
                        text={`Scope ${name}`}
                      />
                    ))}
                  </Grid>
                </DefaultPaper>
              </Grid>
              <Grid item xs={9.5}>
                <DefaultPaper style={{ height: "100%" }}>
                  <Grid container spacing={4} align="center" wrap="nowrap">
                    <Grid item>
                      <Typography variant="h6" align="left">
                        Total Emissions:
                      </Typography>
                      <Typography variant="h4" noWrap>
                        {formatDecimal(sumTonsCo2e(emissions))} tons CO2e
                      </Typography>
                    </Grid>
                    <Grid item>
                      <ColoredAvatarRows rows={coloredAvatarRows} notHalf />
                    </Grid>
                  </Grid>
                </DefaultPaper>
              </Grid>
            </Grid>
            <Grid item>
              <DefaultPaper>
                <Grid container spacing={4}>
                  <Grid item xs={4}>
                    <Typography variant="h5" align="center" gutterBottom>
                      Scope Breakdown
                    </Typography>
                    <EmissionsPieChart
                      dataArray={emissions}
                      viewMode="scopes"
                      aspect={1}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="h5" align="center" gutterBottom>
                      Scope Emissions By{" "}
                      {graphPeriod === "year" ? "Quarter" : "Month"}
                    </Typography>
                    <EmissionsChart
                      type="bar"
                      dataArray={emissions}
                      viewMode="scopes"
                      showTooltip={false}
                      aspect={2}
                    />
                  </Grid>
                </Grid>
              </DefaultPaper>
            </Grid>
            <Grid item>
              <DefaultPaper>
                <Grid container spacing={2}>
                  <Grid item>
                    <Typography variant="h6">Emission Intensities: </Typography>
                  </Grid>
                  <Grid item>
                    <Grid container spacing={1} direction="column">
                      {carbonIntensities.map(({ icon, text }, idx) => (
                        <Grid
                          item
                          container
                          spacing={2}
                          key={`intensity-row-${idx}`}
                          alignItems="center"
                        >
                          <Grid item>
                            <Avatar
                              style={{
                                backgroundColor: palette.secondary.main,
                                color: "white",
                                height: "30px",
                                width: "30px"
                              }}
                            >
                              <FontAwesomeIcon icon={icon} size="xs" />
                            </Avatar>
                          </Grid>
                          <Grid item>
                            <Typography variant="body2">{text}</Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </DefaultPaper>
            </Grid>
          </Grid>
        </GrayPaper>
      </Grid>
      {coloredAvatarRows.some(({ scopeTonsSum }) => !scopeTonsSum) && (
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            *This report doesn't include detailed emissions data for this scope.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

const ScopeCategoryDescriptionRow = ({ name, subcategory, companyName }) => {
  return (
    <Typography
      variant="caption"
      display="block"
      color="textSecondary"
      gutterBottom
      style={{ lineHeight: 1.6 }}
    >
      <strong>{name}:</strong>{" "}
      {buildReportSubcategoryDescriptions(companyName, subcategory)}
    </Typography>
  );
};

const buildCategoriesListRowsArray = (title, categories, companyName) => {
  if (!categories.length) {
    return [];
  }

  const [firstCategory, ...otherCategories] = categories;
  const titleRow = (
    <div style={{ marginTop: "8px" }}>
      <Typography variant="h6" color="textPrimary" gutterBottom>
        {title}
      </Typography>
      <ScopeCategoryDescriptionRow
        companyName={companyName}
        {...firstCategory}
      />
    </div>
  );

  if (!otherCategories.length) {
    return [titleRow];
  }

  return [
    titleRow,
    ...otherCategories.map((category) => (
      <ScopeCategoryDescriptionRow companyName={companyName} {...category} />
    ))
  ];
};

const buildScopeDisplayBlocks = ({ name: scope, emissions, companyName }) => {
  const scopeDescriptions = [
    `Scope 1 emissions are derived directly from fossil fuels that ${companyName} burns. This includes emissions from company-owned vehicles, equipment, and gas utilities for heating and cooking in company-occupied facilities and any other direct emissions from its operations.`,
    `Scope 2 emissions are derived from other companies (usually utilities) burning fossil fuels for the electricity that ${companyName} consumes. This includes emissions from the electricity consumed by the organization's facilities and provided by its electric utility.`,
    `Scope 3 emissions are indirect from your ${companyName}’s purchases and any other emissions that aren't directly derived from fossils that it consumes directly. This includes emissions from its supply chain, employee commuting, business travel, waste disposal, and any other indirect emissions.`
  ];

  // const allScopeSubcategories = subcategories
  //   .filter(
  //     ({ subcategory }) =>
  //       subcategory !== "travel" && subcategory !== "renewable-energy"
  //   )
  //   .filter((subcategory) => subcategory.scope === scope);

  const scopeEmissionSubcategories = buildSubcategoriesArray(emissions);

  const calculateRenewableCarbonTons = (emissions, subcategory) => {
    const subcategoryEmissions = filterEmissionsBySubcategory(
      emissions,
      subcategory
    );

    return subcategoryEmissions.reduce(
      (sum, emission) => sum + Number(emission.renewableCarbonTonsCo2e || 0),
      0
    );
  };

  const coloredAvatarRows = scopeEmissionSubcategories
    .flatMap(({ color, name, subcategory }) => {
      const totalCategoryTons = sumTonsCo2e(
        filterEmissionsBySubcategory(emissions, subcategory)
      );

      if (subcategory === "electricity") {
        const totalCategoryRenewableTons = calculateRenewableCarbonTons(
          emissions,
          subcategory
        );

        const marketBasedTons = totalCategoryTons - totalCategoryRenewableTons;

        return [
          {
            color,
            text: `${name} (Location): ${formatDecimal(
              totalCategoryTons
            )} tCO2e`,
            totalCategoryTons
          },
          {
            color,
            text: `${name} (Market): ${formatDecimal(marketBasedTons)} tCO2e`,
            totalCategoryTons: marketBasedTons
          }
        ];
      }

      return {
        color,
        text: `${name}: ${formatDecimal(totalCategoryTons)} tCO2e`,
        totalCategoryTons
      };
    })
    .sort((a, b) => b.totalCategoryTons - a.totalCategoryTons);

  // const excludedSubcategories = allScopeSubcategories.filter(
  //   ({ name }) =>
  //     !scopeEmissionSubcategories.find(
  //       ({ name: subcatName }) => subcatName === name
  //     )
  // );
  const totalScopeEmissions = sumTonsCo2e(emissions);

  if (!totalScopeEmissions) {
    return null;
  }

  return {
    blocks: [
      <PageHeaderText
        title={`Scope ${scope} Emissions`}
        style={{ marginBottom: "32px" }}
      >
        {scopeDescriptions[scope - 1]}
      </PageHeaderText>,
      <GrayPaper style={{ marginBottom: "32px" }}>
        <Grid container spacing={2} direction="column">
          <Grid item>
            <DefaultPaper style={{ height: "100%" }}>
              <Typography variant="h6">
                Total Scope {scope} Emissions:{" "}
                <strong>{formatDecimal(totalScopeEmissions)} tons CO2e</strong>
              </Typography>
              <ColoredAvatarRows rows={coloredAvatarRows} />
            </DefaultPaper>
          </Grid>
          <Grid item>
            <DefaultPaper style={{ height: "265px" }}>
              <Typography variant="h6" align="center" gutterBottom>
                Scope {scope} Emissions by Category
              </Typography>
              <EmissionsChart
                dataArray={emissions}
                type="bar"
                showTooltip={false}
              />
            </DefaultPaper>
          </Grid>
        </Grid>
      </GrayPaper>,
      ...buildCategoriesListRowsArray(
        `Included Categories`,
        scopeEmissionSubcategories,
        companyName
      )
      //we're commenting this out until we fix the fact that scope three categories aren't assigend properly for activity based vendor emissions
      // ...buildCategoriesListRowsArray(
      //   `Excluded Categories`,
      //   excludedSubcategories,
      //   companyName
      // ),
    ]
  };
};

const OffsetCard = ({
  name,
  shortName,
  image,
  portfolioImage,
  portfolioName,
  images = [],
  setRefElement
}) => (
  <Grid item xs={4}>
    <Paper ref={setRefElement} sx={{ height: "100%", padding: 2 }}>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Box
            style={{
              height: "125px",
              backgroundImage: `url('${image || portfolioImage || images[0]}')`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
              borderRadius: "8px"
            }}
            p={1}
            position="relative"
            display="flex"
            justifyContent="flex-end"
          />
        </Grid>
        <Grid item>
          <Typography variant="h6">
            {truncateText(shortName || name || portfolioName, 40)}
            {portfolioName && " Portfolio"}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  </Grid>
);

const useOffsetsPages = (graphPeriod, year) => {
  const [offsets] = useCachedDisplayData("offsets");

  const buildDisplayOffsetsArray = useCallback(() => {
    const filteredOffsets = offsets.filter(
      ({ attributionYear, badgeYears = [] }) =>
        attributionYear === year ||
        badgeYears.find((badgeYear) => badgeYear.year === year)
    );

    const uniqueOffsets = [
      ...new Set(
        filteredOffsets.map(
          ({ purchaseSlug, portfolioId }) => purchaseSlug || portfolioId
        )
      )
    ];
    console.log("uniqueOffsets", uniqueOffsets);

    return uniqueOffsets.map((project) => {
      const { purchaseSlug, portfolioId } = project;
      const projectData =
        offsets.find(
          ({ slug: projectPurchaseSlug, portfolioId: projectPortfolioId }) =>
            projectPurchaseSlug === purchaseSlug ||
            projectPortfolioId === portfolioId
        ) || {};

      return { ...project, ...projectData };
    });
  }, [offsets, year]);

  const displayOffsets = useMemo(
    () => buildDisplayOffsetsArray(),
    [buildDisplayOffsetsArray]
  );
  console.log("display offsets: ", displayOffsets);

  const [offsetPages, setOffsetPages] = useState([]);

  const projectCardRef = useRef([]);
  const setRefElement = (idx) => (element) => {
    projectCardRef.current[idx] = element;
  };

  useEffect(() => {
    if (displayOffsets.length && !offsetPages.length) {
      setOffsetPages([displayOffsets]);
    }
  }, [displayOffsets, offsetPages.length]);

  useEffect(() => {
    const calcAndSetOffsetPages = () => {
      const CONTENT_HEIGHT = 700;

      const cardHeight = projectCardRef.current[0]?.offsetHeight;
      const numDisplayOffsets = projectCardRef.current.length;
      const numRows = Math.floor(numDisplayOffsets / 3);

      if (cardHeight * numRows < CONTENT_HEIGHT) {
        return setOffsetPages([displayOffsets]);
      }

      const numPages = Math.ceil((cardHeight * numRows) / CONTENT_HEIGHT);
      const rowsPerPage = Math.floor(CONTENT_HEIGHT / cardHeight);

      const pagesArray = [...new Array(numPages)].map((_, idx) =>
        [...displayOffsets].slice(
          idx * rowsPerPage * 3,
          (idx + rowsPerPage) * 3
        )
      );

      return setOffsetPages(pagesArray);
    };

    if (projectCardRef.current.length) {
      calcAndSetOffsetPages();
    }
  }, [displayOffsets, projectCardRef.current.length]);

  if (!displayOffsets.length || graphPeriod === "quarter") {
    return [];
  }

  return offsetPages.map((offsetsArray, idx) => ({
    content: (
      <Grid container direction="column" spacing={2}>
        {!idx && (
          <>
            <Grid item>
              <PageHeaderText title="Offset Projects">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
                eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
                enim ad minim veniam, quis nostrud exercitation ullamco laboris
                nisi ut aliqui.
              </PageHeaderText>
            </Grid>
          </>
        )}
        <Grid item>
          <GrayPaper>
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              justifyContent="center"
            >
              {offsetsArray.map((offset, idx) => (
                <OffsetCard
                  setRefElement={setRefElement(idx)}
                  {...offset}
                  key={`offset-card-i${idx}`}
                />
              ))}
            </Grid>
          </GrayPaper>
        </Grid>
      </Grid>
    )
  }));
};

const BrandedTableCell = ({ children, ...props }) => {
  const { palette } = useTheme();

  return (
    <TableCell
      {...props}
      style={{ border: `solid thin ${palette.backgroundGray.main}` }}
    >
      {children}
    </TableCell>
  );
};

const BrandedReportTable = ({ columns, rows, RowComponent, setRefElement }) => {
  const [{ branding }] = useAccountData();
  const { primaryColor } = branding || {};
  const { palette } = useTheme();

  const headerColor = primaryColor || palette.primary.main;

  return (
    <TableContainer ref={(element) => setRefElement && setRefElement(element)}>
      <Table>
        <TableHead
          style={{
            backgroundColor: headerColor,
            color: isColorDarkTheme(headerColor) ? "white" : "inherit"
          }}
        >
          {columns.map((column, idx) => (
            <TableCell
              key={`table-head-cell-${idx}`}
              style={{
                color: "inherit",
                border: `solid thin white`
              }}
            >
              <strong>{column}</strong>
            </TableCell>
          ))}
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <RowComponent {...row} key={`table-row-${idx}`} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const useWrappedTableRows = ({
  rows,
  columns,
  titleBlockHeight,
  title,
  headerText,
  RowComponent
}) => {
  const TITLE_BLOCK_HEIGHT = titleBlockHeight || 82;

  const [tableRows, setTableRows] = useState([[]]);
  const blocksRef = useRef([]);

  const setRefElement = (idx) => (element) => {
    blocksRef.current[idx] = element;
  };

  useEffect(() => {
    if (!tableRows[0].length && rows.length) {
      return setTableRows([rows]);
    }
  }, [setTableRows, rows, tableRows]);

  useEffect(() => {
    const buildSplitTableData = () => {
      const heights = blocksRef.current.map((ref) => ref?.offsetHeight);

      const tooTallTableIdx = heights.findIndex(
        (height) => height > REPORT_CONTENT_HEIGHT - TITLE_BLOCK_HEIGHT
      );

      if (tooTallTableIdx === -1) {
        return;
      }

      //this is a super complicated mutable function that was written with the help of Chat GPT
      //https://chatgpt.com/share/e/6801ee0a-c3bc-8000-a80f-12ca6a382bea
      //basically it iteratively shifts the last row of the table that is too tall to the next table while still maintaining the order of the rows
      const shiftArray = (array) => {
        let shifted = false;
        let skipIndex = -1;

        const newArrays = array.reduce((acc, current, index, original) => {
          if (index === skipIndex) {
            return acc;
          }

          if (shifted || index !== tooTallTableIdx) {
            return [...acc, current];
          }

          const clone = [...current];
          const lastRow = clone.pop();
          shifted = true;

          acc.push(clone);

          if (original[index + 1]) {
            acc.push([lastRow, ...original[index + 1]]);
            skipIndex = index + 1;
          } else {
            acc.push([lastRow]);
          }

          return acc;
        }, []);

        return newArrays.filter((array) => array.length > 0);
      };

      return setTableRows((tableRows) => shiftArray(tableRows));
    };

    let timeout;

    if (blocksRef.current.length) {
      timeout = setTimeout(() => buildSplitTableData(), 300);
    }

    return () => clearTimeout(timeout);
  }, [
    blocksRef.current.length,
    tableRows,
    titleBlockHeight,
    TITLE_BLOCK_HEIGHT
  ]);

  const tableRowProps = {
    columns,
    RowComponent
  };

  return tableRows.map((tableRows, idx) => ({
    content: idx ? (
      <BrandedReportTable
        {...tableRowProps}
        rows={tableRows}
        key={`table-${idx}`}
        setRefElement={setRefElement(idx)}
      />
    ) : (
      <Grid container spacing={2} direction="column" key={`table-${idx}`}>
        <Grid item>
          <PageHeaderText title={title}>{headerText}</PageHeaderText>
        </Grid>
        <Grid item>
          <BrandedReportTable
            {...tableRowProps}
            rows={tableRows}
            setRefElement={setRefElement(idx)}
          />
        </Grid>
      </Grid>
    )
  }));
};

const ScopeThreeCategoryRow = ({ name, totalTons }) => (
  <TableRow>
    <BrandedTableCell>{name}</BrandedTableCell>
    <BrandedTableCell>{formatDecimal(totalTons)}</BrandedTableCell>
  </TableRow>
);

const useScopeThreeCategoriesTables = (emissions) => {
  const scopeThreeEmissions = filterEmissionsByScope(emissions, 3);

  const formattedScopeThreeEmissions = scopeThreeEmissions.map((emission) => {
    const { subcategory } = emission;
    if (
      scopeThreeSpendBasedCategories.find(
        ({ subcategory: catSubcategory }) => catSubcategory === subcategory
      )
    ) {
      return emission;
    }

    const { scopeThreeCategory } = subcategories.find(
      ({ subcategory: catSubcategory }) => catSubcategory === subcategory
    ) || { scopeThreeCategory: null };

    const { subcategory: scopeThreeCategorySubcategory } =
      scopeThreeSpendBasedCategories.find(
        (category) => category.scopeThreeCategory === scopeThreeCategory
      ) || { subcategory: "Other" };

    return { ...emission, subcategory: scopeThreeCategorySubcategory };
  });

  const emissionsByCategory = scopeThreeSpendBasedCategories.map((category) => {
    const emissionsBySubcategory = formattedScopeThreeEmissions.filter(
      ({ subcategory }) => subcategory === category.subcategory
    );
    const totalTons = sumTonsCo2e(emissionsBySubcategory);
    return {
      ...category,
      totalTons
    };
  });

  return useWrappedTableRows({
    rows: emissionsByCategory,
    columns: ["Scope 3 Category", "Tonnes CO2e Total"],
    title: "Scope 3 Breakdown",
    RowComponent: ScopeThreeCategoryRow,
    headerText:
      "This report tracks indirect emissions across all upstream and downstream activities. It provides a comprehensive accounting of environmental impacts throughout the entire value chain."
  });
};

const ShippingBreakdownRow = ({
  name,
  upstreamTons,
  downstreamTons,
  totalTons,
  isTotal
}) => (
  <TableRow>
    <BrandedTableCell>
      {isTotal ? <strong>{name}</strong> : name}
    </BrandedTableCell>
    <BrandedTableCell>
      {isTotal ? (
        <strong>{formatDecimal(upstreamTons)}</strong>
      ) : (
        formatDecimal(upstreamTons)
      )}
    </BrandedTableCell>
    <BrandedTableCell>
      {isTotal ? (
        <strong>{formatDecimal(downstreamTons)}</strong>
      ) : (
        formatDecimal(downstreamTons)
      )}
    </BrandedTableCell>
    <BrandedTableCell>
      {isTotal ? (
        <strong>{formatDecimal(totalTons)}</strong>
      ) : (
        formatDecimal(totalTons)
      )}
    </BrandedTableCell>
  </TableRow>
);

const useShippingBreakdownTable = (emissions) => {
  const shippingEmissions = findShippingEmissions(emissions);
  const knownMethodValues = shippingTransportationMethods.map(
    ({ value }) => value
  );

  const methodRows = shippingTransportationMethods
    .map(({ value, label }) => {
      const methodEmissions = shippingEmissions.filter(
        ({ transportationMethod }) => transportationMethod === value
      );
      return buildShippingMethodRow(label, methodEmissions);
    })
    .filter(({ totalTons }) => totalTons !== 0);

  const unknownEmissions = shippingEmissions.filter(
    ({ transportationMethod }) =>
      !knownMethodValues.includes(transportationMethod)
  );

  const unknownRows = unknownEmissions.length
    ? [buildShippingMethodRow("Unknown", unknownEmissions)]
    : [];

  const sortedRows = [...methodRows, ...unknownRows].sort(
    (a, b) => b.totalTons - a.totalTons
  );

  const totalUpstreamTons = sumTonsCo2e(
    shippingEmissions.filter((e) => findShippingDirection(e) === "upstream")
  );
  const totalDownstreamTons = sumTonsCo2e(
    shippingEmissions.filter((e) => findShippingDirection(e) === "downstream")
  );

  const rows = [
    ...sortedRows,
    {
      name: "Total",
      upstreamTons: totalUpstreamTons,
      downstreamTons: totalDownstreamTons,
      totalTons: totalUpstreamTons + totalDownstreamTons,
      isTotal: true
    }
  ];

  return useWrappedTableRows({
    rows: shippingEmissions.length ? rows : [],
    columns: [
      "Mode",
      "Upstream (tCO2e)",
      "Downstream (tCO2e)",
      "Total (tCO2e)"
    ],
    title: "Shipping Emissions Breakdown",
    RowComponent: ShippingBreakdownRow,
    headerText:
      "This table shows shipping emissions broken down by transportation mode and direction. Upstream shipping covers supply chain deliveries to your company; downstream covers distribution to your customers."
  });
};

const FacilitiesTableRow = ({ name, ...otherProps }) => (
  <TableRow>
    <BrandedTableCell>{name}</BrandedTableCell>
    {Object.values(otherProps).map((value, idx) => (
      <BrandedTableCell key={`facility-row-cell-${idx}`}>
        {formatDecimal(value)}
      </BrandedTableCell>
    ))}
  </TableRow>
);

const useFacilitiesSummaryTable = ({ startDate, endDate }) => {
  const [offices] = useOfficesWithCurrentChanges();
  const {
    separateUtilitiesOfficesDefaultEmissions,
    monthlyUtilitiesEmissionsTons,
    utilitiesTransactions
  } = useEmissionsContext();

  const periodFilteredOffices = filterOfficesByPeriod({
    offices,
    startDate,
    endDate
  });

  const sumOfficeEmissionsProperty = (
    emissions,
    property,
    alternateProperty
  ) => {
    const extractEmissionProperty = (emission) => {
      const { emissionData = {}, details } = emission;
      const emissionWithData = { ...emission, ...emissionData, ...details };
      const emissionProperty =
        emissionWithData?.[property] ??
        emissionWithData?.[alternateProperty] ??
        0;

      if (typeof emissionProperty === "number") {
        return emissionProperty;
      }

      return Number(emissionProperty.replaceAll(",", ""));
    };

    return emissions.reduce((sum, emission) => {
      const newValue = extractEmissionProperty(emission);

      return sum + Number(newValue);
    }, 0);
  };

  const buildTableData = () => {
    if (!periodFilteredOffices.length) {
      return [];
    }

    const officeFormattedEmissions = periodFilteredOffices.map(
      ({ name, id }) => {
        const officeDefaultEmissions =
          separateUtilitiesOfficesDefaultEmissions.filter(
            ({ taggedOfficeId, subcategory }) =>
              taggedOfficeId === id && subcategory === "electricity"
          );

        const taggedRecurringEmissions = monthlyUtilitiesEmissionsTons.filter(
          ({ office, subcategory }) =>
            office?.id === id && subcategory === "electricity"
        );

        const taggedUtilitiesTransactions = utilitiesTransactions.filter(
          ({ office, subcategory }) =>
            office?.id === id && subcategory === "electricity"
        );

        const officeEmissions = [
          ...officeDefaultEmissions,
          ...taggedRecurringEmissions,
          ...taggedUtilitiesTransactions
        ];

        const dateFilteredEmissions = officeEmissions.filter(({ date }) =>
          filterByDateRange({
            filterStartDate: startDate,
            filterEndDate: endDate,
            date,
            unit: "day"
          })
        );

        const totalEmittedTonsCo2e = sumTonsCo2e(dateFilteredEmissions);

        const recsTons = sumOfficeEmissionsProperty(
          dateFilteredEmissions,
          "renewableCarbonTonsCo2e"
        );

        const totalTonsCo2e = totalEmittedTonsCo2e + recsTons;

        const totalEmissionsKwh = sumOfficeEmissionsProperty(
          dateFilteredEmissions,
          "electricKwh",
          "totalKwh"
        );

        const totalEmissionsRecsKwh = sumOfficeEmissionsProperty(
          dateFilteredEmissions,
          "recsKwh"
        );

        return {
          name,
          totalEmissionsKwh,
          totalTonsCo2e,
          totalEmissionsRecsKwh,
          adjustedTonsCo2e: totalEmittedTonsCo2e
        };
      }
    );

    const totalsRowValues = Object.fromEntries(
      [
        "totalEmissionsKwh",
        "totalTonsCo2e",
        "totalEmissionsRecsKwh",
        "adjustedTonsCo2e"
      ].map((property) => {
        const totalValue = officeFormattedEmissions.reduce(
          (sum, office) => sum + Number(office[property] || 0),
          0
        );
        return [property, totalValue];
      })
    );

    const totalsRow = { name: "Totals", ...totalsRowValues };

    return [...officeFormattedEmissions, totalsRow];
  };

  return useWrappedTableRows({
    rows: buildTableData(),
    columns: [
      "Facility Name",
      "kWh Total",
      "Location Based Total tCO2e",
      "Market Based REC kWh",
      "Market Based Adjusted tCO2e"
    ],
    title: "Location and Market-Based Scope 2 By Facility",
    RowComponent: FacilitiesTableRow,
    headerText:
      "This report presents Scope 2 emissions using both location-based and market-based methods. It tracks each facility’s energy use and renewable energy procurement to provide a complete picture of total emissions."
  });
};

const NameCell = ({ scopeIdx, name, rowSpan }) => {
  return (
    <>
      {scopeIdx === 0 && (
        <BrandedTableCell rowSpan={rowSpan}>{name}</BrandedTableCell>
      )}
    </>
  );
};

const FacilitiesDetailsTableRow = ({
  name,
  emissions,
  totalTons,
  totalPercentage
}) => {
  const totalSubcategoryRows = emissions.reduce(
    (sum, { subcategoryEmissions }) => sum + subcategoryEmissions.length,
    0
  );

  const totalsRow = [
    "Location Total",
    formatDecimal(totalTons),
    `${formatDecimal(totalPercentage * 100)}%`
  ];

  return (
    <>
      {emissions.map(({ scope, subcategoryEmissions }, scopeIdx) => {
        return subcategoryEmissions.map(
          ({ subcategory, totalTons, percentage }, subcategoryIdx) => {
            return (
              <TableRow>
                <NameCell
                  scopeIdx={scopeIdx}
                  name={name}
                  rowSpan={totalSubcategoryRows}
                />
                {!subcategoryIdx && (
                  <BrandedTableCell rowSpan={subcategoryEmissions.length}>
                    Scope {scope}
                  </BrandedTableCell>
                )}
                <BrandedTableCell>
                  {ucFirstLetters(subcategory)}
                </BrandedTableCell>
                <BrandedTableCell>{formatDecimal(totalTons)}</BrandedTableCell>
                <BrandedTableCell>
                  {formatDecimal(percentage * 100)}%
                </BrandedTableCell>
              </TableRow>
            );
          }
        );
      })}
      <TableRow>
        {totalsRow.map((value, idx) => (
          <BrandedTableCell
            key={`totals-row-cell-${idx}`}
            colSpan={!idx ? 3 : 1}
          >
            <strong>{value}</strong>
          </BrandedTableCell>
        ))}
      </TableRow>
    </>
  );
};

const useWrappedFacilitiesDetailsTable = ({
  offices,
  totalReportPeriodTons,
  startDate,
  endDate
}) => {
  const formattedTableData = offices.map(
    ({ emissions: officeEmissions, name }) => {
      const dateFilteredEmissions = officeEmissions.filter(({ date }) =>
        filterByDateRange({
          filterStartDate: startDate,
          filterEndDate: endDate,
          date,
          unit: "day"
        })
      );

      const emissionsByScope = scopesList.map(({ name: scope }) => ({
        scope,
        scopeEmissions: filterEmissionsByScope(dateFilteredEmissions, scope)
      }));

      const emissionsBySubcategory = emissionsByScope.map(
        ({ scope, scopeEmissions }) => {
          if (!scopeEmissions.length) {
            return {
              scope,
              subcategoryEmissions: [
                {
                  subcategory: scope === 1 ? "gas" : "electricity",
                  totalTons: 0,
                  percentage: 0
                }
              ]
            };
          }

          const uniqueSubcategories = [
            ...new Set(scopeEmissions.map(({ subcategory }) => subcategory))
          ];

          const subcategoryEmissions = uniqueSubcategories.map(
            (subcategory) => {
              const emissionsBySubcategory = filterEmissionsBySubcategory(
                dateFilteredEmissions,
                subcategory
              );
              const totalTons = sumTonsCo2e(emissionsBySubcategory);

              const percentage = totalTons / totalReportPeriodTons;

              return {
                subcategory,
                totalTons,
                percentage
              };
            }
          );

          return { scope, subcategoryEmissions };
        }
      );

      const officeTotalTons = sumTonsCo2e(dateFilteredEmissions);
      return {
        name,
        emissions: emissionsBySubcategory,
        totalTons: officeTotalTons,
        totalPercentage: officeTotalTons / totalReportPeriodTons
      };
    }
  );

  return useWrappedTableRows({
    rows: formattedTableData,
    columns: [
      "Facility Name",
      "Scope",
      "Activity Type",
      "tCO2e",
      "% of Total Inventory"
    ],
    title: "Facility Scope Emissions Summary",
    RowComponent: FacilitiesDetailsTableRow,
    headerText:
      "This report provides a summary of greenhouse gas emissions by facility, categorized by scope and activity type. It tracks each facility’s contribution to the total emissions inventory and highlights key sources of emissions."
  });
};

const DisclaimersPage = ({ companyName }) => (
  <Grid container spacing={4}>
    <Grid item>
      <PageHeaderText title="Disclaimers">
        This report is based on data provided by {companyName} and calculated
        using Aclymate’s carbon accounting software, which applies recognized
        emission factors and methodologies. All calculations are dependent on
        the completeness and accuracy of the input data supplied by the company
        and its vendors. Emissions calculations were generated using Aclymate’s
        software tools, which align with GHG Protocol guidance to the extent
        possible. This report has not been independently verified or audited by
        a third party. This report is intended for informational and planning
        purposes only. It should not be used as a definitive statement of legal
        compliance, regulatory reporting, or investment advice without
        additional expert review.
      </PageHeaderText>
    </Grid>
    <Grid item>
      <GrayPaper>
        <Grid container spacing={4} direction="column">
          <Grid item>
            <Typography variant="body1" align="center">
              This report was prepared by <strong>Aclymate</strong>,{" "}
              {companyName}'s trusted partner in measuring, reducing, and
              reporting carbon emissions.
            </Typography>
          </Grid>
          <Grid item container spacing={4} alignItems="stretch">
            <Grid item xs={5}>
              <DefaultPaper
                style={{ height: "250px" }}
                boxStyle={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <img
                  alt="Aclymate Logo"
                  src="/images/aclymate-vertical-logo.png"
                  style={{ width: "80%" }}
                />
              </DefaultPaper>
            </Grid>
            <Grid item xs={7}>
              <Box
                style={{ height: "100%" }}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Typography variant="body1">
                  If you have any questions about this emissions data, need
                  assistance with regulatory compliance, or want to explore our
                  sustainability solutions, please contact us.
                </Typography>
                <Grid container direction="column" spacing={1}>
                  {[
                    { icon: faGlobe, text: "aclymate.com" },
                    { icon: faEnvelope, text: "info@aclymate.com" },
                    { icon: faPhone, text: "+1 (303) 390-1248" }
                  ].map(({ icon, text }) => (
                    <Grid item container spacing={2} wrap="nowrap">
                      <Grid item>
                        <FontAwesomeIcon icon={icon} />
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">{text}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </GrayPaper>
    </Grid>
  </Grid>
);

const ReportPages = ({
  startDate,
  endDate,
  emissions: rawEmissions,
  graphPeriod,
  pdfLoading
}) => {
  const [{ name: companyName }, accountDataLoading] = useAccountData();
  const { offices, officesLoading } = useOfficesData();

  const [periodTransactions, setPeriodTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  useEffect(() => {
    setTransactionsLoading(true);
    fetchPeriodTransactions({ startDate, endDate })
      .then((transactions) => {
        setPeriodTransactions(Array.isArray(transactions) ? transactions : []);
        setTransactionsLoading(false);
      })
      .catch(() => {
        setTransactionsLoading(false);
      });
  }, [startDate, endDate]);

  const emissions = useMemo(
    () => expandUtilitiesEmissions(rawEmissions),
    [rawEmissions]
  );

  const periodFilteredOffices = filterOfficesByPeriod({
    offices,
    startDate,
    endDate
  });

  const totalReportPeriodTons = sumTonsCo2e(emissions);

  const scopeThreeCategoriesTables = useScopeThreeCategoriesTables(emissions);
  const shippingBreakdownPages = useShippingBreakdownTable(periodTransactions);

  const offsetsPages = useOffsetsPages(
    graphPeriod,
    Number(dayjs(startDate).year())
  );

  const facilitiesSummaryTables = useFacilitiesSummaryTable({
    startDate,
    endDate
  });

  const wrappedFacilitiesDetailsTables = useWrappedFacilitiesDetailsTable({
    offices: periodFilteredOffices,
    totalReportPeriodTons,
    startDate,
    endDate
  });

  const reportPages = [
    { content: <TitlePage startDate={startDate} endDate={endDate} /> },
    {
      content: (
        <EmissionsSummaryPage
          emissions={emissions}
          startDate={startDate}
          endDate={endDate}
          totalReportPeriodTons={totalReportPeriodTons}
          offices={periodFilteredOffices}
          graphPeriod={graphPeriod}
        />
      )
    },
    ...scopesList.map((scope) =>
      buildScopeDisplayBlocks({
        ...scope,
        emissions: filterEmissionsByScope(emissions, scope.name),
        companyName
      })
    ),
    ...offsetsPages,
    ...scopeThreeCategoriesTables,
    ...(transactionsLoading
      ? [{ content: <AnimatedLogo /> }]
      : findShippingEmissions(periodTransactions).length
        ? shippingBreakdownPages
        : []),
    ...(periodFilteredOffices.length ? facilitiesSummaryTables : []),
    ...(periodFilteredOffices.length ? wrappedFacilitiesDetailsTables : []),
    { content: <DisclaimersPage companyName={companyName} /> }
  ];

  return (
    <>
      {!(accountDataLoading || officesLoading) &&
        reportPages
          .filter(Boolean)
          .map((pageRow, idx) => (
            <ReportPage
              key={`report-page-${idx}`}
              startDate={startDate}
              endDate={endDate}
              showHeader={!!idx}
              pdfLoading={pdfLoading}
              pageNumber={idx + 1}
              totalPages={reportPages.length}
              {...pageRow}
            />
          ))}
    </>
  );
};

const Report = ({ setShowReport, startDate, endDate, ...otherProps }) => {
  const [{ name: companyName = "" }, companyDataLoading] = useAccountData();
  const [accountingYears] = useCachedDisplayData("accounting");

  const reportYear = dayjs(startDate).year();
  const lockedYears = (accountingYears || []).map(({ date }) =>
    dayjs(date?.toDate ? date.toDate() : date).year()
  );
  const isFinalized = lockedYears.includes(reportYear);

  if (companyDataLoading) {
    return <></>;
  }

  return (
    <ReportLayout
      setShowReport={setShowReport}
      ReportComponent={ReportPages}
      initialName={`${companyName} ${formatDate(startDate)} - ${formatDate(
        endDate
      )} Emissions`}
      reportProps={{ startDate, endDate, ...otherProps }}
      isFinalized={isFinalized}
    />
  );
};
export { ReportPages };
export default Report;
